import { gatedFetch } from '../utils/requestGate';

const API_BASE = process.env.REACT_APP_API_URL || '';
const IMAGE_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV !== 'production' ? 'http://localhost:8081' : '');
const DEFAULT_TIMEOUT_MS = 25000;
const TOKEN_KEY = 'oh_pedidos_token';

let _token = null;

export function setAuthToken(token) {
  _token = token ? String(token) : null;
  try {
    if (_token) sessionStorage.setItem(TOKEN_KEY, _token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getAuthToken() {
  if (_token) return _token;
  try {
    _token = sessionStorage.getItem(TOKEN_KEY) || null;
  } catch {
    _token = null;
  }
  return _token;
}

export function clearAuthToken() {
  _token = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  let res;
  try {
    res = await gatedFetch(`${API_BASE}/api${path}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeaders(),
        ...(options.headers || {}),
      },
      body: options.body,
      signal: controller?.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('La solicitud tardó demasiado. Revisa la conexión e intenta de nuevo.');
    }
    throw new Error('No se pudo conectar con el servidor. Revisa que el API esté Running en cPanel e intenta de nuevo.');
  } finally {
    if (timer) clearTimeout(timer);
  }

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error('El backend no tiene las rutas de Pedidos. Reinicia el API: cd backend → node src/index.js');
    }
    throw new Error(json.error || `Error en la solicitud (${res.status})`);
  }
  return json;
}

export function formatCOP(value) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

export function imageUrl(imagen) {
  if (!imagen) return null;
  if (imagen.startsWith('http')) return imagen;
  const base = IMAGE_BASE || '';
  return `${base}/${imagen.replace(/^\//, '')}`;
}

export const api = {
  ping: () => request('/ping'),
  login: async (username, password) => {
    const res = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        username: String(username || '').trim(),
        password: String(password || ''),
        app: 'oficina',
      }),
    });
    if (res.data?.token) setAuthToken(res.data.token);
    return res;
  },
  me: () => request('/auth/me'),
  logout: () => request('/auth/logout', { method: 'POST' }).finally(() => clearAuthToken()),
  getVendedores: () => request('/vendedores'),
  getPedidos: ({ estado, q, vendedor_id, limit = 100 } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (estado) params.set('estado', estado);
    if (q) params.set('q', q);
    if (vendedor_id) params.set('vendedor_id', String(vendedor_id));
    return request(`/admin/pedidos?${params}`);
  },
  getAdminPedidos: (
    { estado, q, desde, hasta, limit = 100, resumen = false, por, vendedor_id, preparado_por_id } = {}
  ) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (estado) params.set('estado', estado);
    if (q) params.set('q', q);
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    if (resumen) params.set('resumen', '1');
    if (por) params.set('por', por);
    if (vendedor_id) params.set('vendedor_id', String(vendedor_id));
    if (preparado_por_id) params.set('preparado_por_id', String(preparado_por_id));
    return request(`/admin/pedidos?${params}`);
  },
  getPedido: (id) => request(`/admin/pedidos/${id}`),
  getAdminPedido: (id) => request(`/admin/pedidos/${id}`),
  getUsuarios: () => request('/admin/usuarios'),
  getHistorial: ({ fecha } = {}) => {
    const params = new URLSearchParams({ estado: 'entregado', limit: '50', por: 'preparado' });
    if (fecha) {
      params.set('desde', fecha);
      params.set('hasta', fecha);
    }
    return request(`/admin/pedidos?${params}`);
  },
  actualizarEstado: (id, estado, motivo) =>
    request(`/admin/pedidos/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify(motivo ? { estado, motivo } : { estado }),
    }),
  actualizarItems: (id, items) =>
    request(`/admin/pedidos/${id}/items`, {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    }),
  getAdminSucursales: ({
    q = '',
    ciudad = '',
    zona = '',
    vendedor_id,
    tipo = '',
    limit = 50,
    offset = 0,
    facets,
  } = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (q) params.set('q', q);
    if (ciudad) params.set('ciudad', ciudad);
    if (zona) params.set('zona', zona);
    if (vendedor_id != null && vendedor_id !== '') params.set('vendedor_id', String(vendedor_id));
    if (tipo) params.set('tipo', String(tipo));
    if (facets) params.set('facets', '1');
    return request(`/sucursales?${params}`, { timeoutMs: 40000 });
  },
  crearSucursal: (body) =>
    request('/sucursales', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSedes: (clienteId) => request(`/sucursales/${clienteId}/sedes`),
  crearSede: (clienteId, body) =>
    request(`/sucursales/${clienteId}/sedes`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  actualizarSede: (clienteId, sedeId, body) =>
    request(`/sucursales/${clienteId}/sedes/${sedeId}`, {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    }),
  eliminarSede: (clienteId, sedeId) =>
    request(`/sucursales/${clienteId}/sedes/${sedeId}`, { method: 'DELETE' }),
  importarClientesTxt: (body) =>
    request('/admin/clientes/importar-txt', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  importarSucursalesTxt: (body) =>
    request('/admin/clientes/importar-sucursales-txt', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  getClientesImportaciones: () => request('/admin/clientes/importaciones'),
  importarInventario: (body) =>
    request('/admin/inventario/importar', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    }),
  getInventarioCambios: ({ codigo = '', limit = 100 } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (codigo) params.set('codigo', codigo);
    return request(`/admin/inventario/cambios?${params}`);
  },
  getInventarioImportaciones: () => request('/admin/inventario/importaciones'),
  getInventarioConfig: () => request('/admin/inventario/config'),
};
